import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Eye } from 'lucide-react';
import { WorkspaceButton } from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import type { QuestionnairePreview as PreviewData } from '@/lib/api/discovery-frameworks';
import { ClientCard, ClientFrame } from './client-frame';
import { QuestionnaireForm } from './questionnaire-form';

/**
 * Consultant preview of a questionnaire as its respondent sees it
 * (prototype `clientQuestionnaire()` with a preview code, E12-F01
 * scenario 18): a dark bar saying it is a preview and that nothing is
 * sent, then the client page itself, with "Send my answers" disabled.
 * It covers the whole window like the page it shows; closing it returns
 * to exactly where the consultant was.
 */

export function QuestionnairePreview({
  data,
  error,
  ofDraft,
  onClose,
}: {
  data: PreviewData | undefined;
  error: unknown;
  ofDraft: boolean;
  onClose: () => void;
}) {
  const p = data?.payload;
  const who = p?.one_per_department ? 'each department head' : 'the person';
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-[70] overflow-y-auto bg-paper-50 focus:outline-none"
        >
          <div className="sticky top-0 z-[1] flex items-center gap-2.5 bg-ink-900 px-6 py-2.5 text-body-sm text-paper-0">
            <Eye className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
            <Dialog.Title className="min-w-0 flex-1 font-normal">
              Preview{ofDraft ? ' of the draft' : ''}: this is what {who} sees from the secure link.
              Nothing is sent.
            </Dialog.Title>
            <WorkspaceButton onClick={onClose}>Close preview</WorkspaceButton>
          </div>
          <ClientFrame
            forLine={
              data?.client_name
                ? `Secure link for ${data.client_name} · no account needed`
                : 'Secure link · no account needed'
            }
          >
            <ClientCard>
              {error ? (
                <ErrorMessage error={error} />
              ) : !p ? (
                <div className="flex items-center gap-2 text-body-sm text-ink-500">
                  <Spinner /> Loading the questionnaire…
                </div>
              ) : (
                <QuestionnaireForm
                  meta={[data?.client_name, p.audience_line].filter(Boolean).join(' · ')}
                  title={p.title}
                  minutes={p.minutes}
                  until="until it expires"
                  questions={p.questions.map((q) => ({ ...q, key: String(q.ordinal) }))}
                  footer={
                    <div className="mt-2 flex justify-end">
                      <WorkspaceButton size="md" variant="primary" disabled title="Preview only">
                        Send my answers
                      </WorkspaceButton>
                    </div>
                  }
                />
              )}
            </ClientCard>
          </ClientFrame>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
